import { useEffect, type ReactNode } from 'react';
import { cn } from '~/lib/cn';

interface SlideOverProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Width in px; defaults to 480. The design uses 480 for a single-column form. */
  width?: number;
}

/**
 * Right-anchored slide-over panel. Backdrop click + Escape close. Body
 * scrollable; `footer` sticks to the bottom (meant for primary CTA + Cancel).
 * The panel hands focus to its own close button on open so Escape works
 * immediately; keyboard users don't have to tab in from behind.
 */
export function SlideOver({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  width = 480,
}: SlideOverProps): JSX.Element | null {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40" role="dialog" aria-modal="true" aria-label={title}>
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-black/35 backdrop-blur-[1px] motion-safe:animate-[fadeIn_120ms_ease-out]"
      />
      <div
        className={cn(
          'absolute right-0 top-0 flex h-full flex-col bg-surface-plate shadow-[0_0_0_1px_rgba(10,10,10,0.08),-24px_0_56px_-24px_rgba(10,10,10,0.35)]',
          'motion-safe:animate-[slideInRight_180ms_cubic-bezier(0.32,0.72,0,1)]',
        )}
        style={{ width }}
      >
        <header className="flex items-start justify-between gap-3 border-b border-rule-hairline px-6 pb-4 pt-5">
          <div>
            <h2 className="text-[16px] font-semibold -tracking-[0.01em] text-ink-primary">
              {title}
            </h2>
            {description && (
              <p className="mt-1 text-[13.5px] leading-[18px] text-ink-secondary">
                {description}
              </p>
            )}
          </div>
          <button
            type="button"
            autoFocus
            aria-label="Close"
            onClick={onClose}
            className="inline-flex h-7 w-7 items-center justify-center rounded-md text-ink-watermark hover:bg-surface-margin hover:text-ink-primary"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round">
              <path d="M3 3l10 10M13 3L3 13" />
            </svg>
          </button>
        </header>
        <div className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
        {footer && (
          <footer className="border-t border-rule-hairline bg-surface-canvas/50 px-6 py-3">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}
